import { DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, Output, ViewChild, input, signal } from '@angular/core';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridApi, GridOptions, GridReadyEvent } from 'ag-grid-community'; // GridReadyEvent used in gridOptions callback
import { GridPersist } from '../../grid/grid-persist';
import {
  DeleteProfessionalDevelopmentRecordCommand,
  ProfessionalDevelopmentRecordResultDto,
  UpdateProfessionalDevelopmentRecordCommand,
} from '../../services/api.service';
import { ProfessionalDevelopmentRecentAwardsActionsCellRenderer } from './pd-recent-awards-actions-cell-renderer/actions-cell/pd-recent-awards-actions-cell-renderer.component';
import { PDSource } from 'src/shared/constants';

@Component({
  selector: 'cert-pd-recent-awards-grid',
  standalone: false,
  templateUrl: './pd-recent-awards-grid.component.html',
})
export class ProfessionalDevelopmentRecentAwardsGridComponent {
  @ViewChild(AgGridAngular) agGrid!: AgGridAngular;

  @Input() leaCodeId = 0;
  @Input() pdRecords: ProfessionalDevelopmentRecordResultDto[] = [];
  @Output() update = new EventEmitter<UpdateProfessionalDevelopmentRecordCommand>();
  @Output() delete = new EventEmitter<DeleteProfessionalDevelopmentRecordCommand>();

  protected gridApi!: GridApi;
  protected errorEmitter = new EventEmitter<any>();
  selectedPDRecordIds = signal<number[]>([]);

  protected colDefs: ColDef<ProfessionalDevelopmentRecordResultDto>[] = [
    {
      field: 'ein',
      headerName: 'EIN',
      width: 150,
    },
    {
      field: 'educatorName',
      headerName: 'Educator Name',
      width: 400,
    },
    {
      field: 'hours',
      headerName: 'Hours',
      width: 125,
    },
    {
      field: 'description',
      headerName: 'Description',
      width: 500,
    },
    {
      field: 'source',
      headerName: 'Source',
      cellRenderer: (params: any) => {
        const cssClass = this.getStatusClass(params.data);
        return `<span class="badge badge-md ${cssClass}">${params.value ?? ''}</span>`;
      },
      width: 150,
    },
    {
      field: 'completionDate',
      headerName: 'Completion Date',
      filter: 'agDateColumnFilter',
      valueFormatter: (params) => this.datePipe.transform(params.value, 'M/d/yyyy') || '',
      width: 200,
    },
    {
      headerName: 'Actions',
      sortable: false,
      cellRenderer: ProfessionalDevelopmentRecentAwardsActionsCellRenderer,
      suppressHeaderMenuButton: true,
      filter: false,
      minWidth: 100,
    },
  ];

  gridPersist = new GridPersist('cert-pd-recent-awards-grid');

  protected gridOptions: GridOptions<ProfessionalDevelopmentRecordResultDto> = this.gridPersist.setup({
    domLayout: 'autoHeight',

    rowSelection: { mode: 'multiRow', enableClickSelection: false, checkboxes: true },
    pagination: true,
    paginationPageSize: 25,
    paginationPageSizeSelector: [10, 25, 50, 100],
    context: { componentParent: this },
    defaultColDef: {
      sortable: true,
      resizable: true,
      suppressMovable: true,
      filter: 'agTextColumnFilter',
    },
    columnDefs: this.colDefs,
    onSelectionChanged: () => {
      this.selectedPDRecordIds.set(this.gridApi?.getSelectedRows().map((r) => r.professionalDevelopmentRecordId) ?? []);
    },
    onGridReady: (params: GridReadyEvent<ProfessionalDevelopmentRecordResultDto>) => {
      this.gridApi = params.api;
    },
    onGridSizeChanged: () => this.gridApi?.sizeColumnsToFit(),
  });

  constructor(private readonly datePipe: DatePipe) {}

  onUpdate(
    description?: string | undefined,
    hours?: number | undefined,
    completionDate?: Date | undefined,
    professionalDevelopmentTypeCodeId?: number | undefined,
  ) {
    if (this.selectedPDRecordIds().length === 0) return;
    this.update.emit({
      professionalDevelopmentRecordIds: [...this.selectedPDRecordIds()],
      leaCodeId: this.leaCodeId,
      description,
      hours,
      completionDate,
      professionalDevelopmentTypeCodeId,
    });
  }

  onDelete() {
    if (this.selectedPDRecordIds().length === 0) return;
    this.delete.emit({ professionalDevelopmentRecordIds: [...this.selectedPDRecordIds()], leaCodeId: this.leaCodeId });
  }

  clearSelection() {
    this.gridApi?.deselectAll();
  }

  resetFilters() {
    if (this.gridApi) {
      this.gridApi.setFilterModel(null);
      this.gridPersist.resetAndSaveGrid(this.gridApi);
    }
  }

  private getStatusClass(credit: ProfessionalDevelopmentRecordResultDto): string {
    if (credit.source === PDSource.LEA) return 'badge-success';
    if (credit.source === PDSource.Educator) return 'badge-warning';
    return 'badge-info';
  }
}
